import { ApolloProvider } from "@apollo/client";
import i18next from "i18next";
import { PrimeReactProvider } from "primereact/api";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { Provider } from "react-redux";
import AppView from "./app/AppView";
import { getGraphQLClient } from "./core/services/graphQLClient";
import "./index.css"; // Ensure Tailwind CSS is imported here
import { store } from "./redux/store";
import reportWebVitals from "./reportWebVitals";
import translations_ar from "./translations/ar.json";
import translations_en from "./translations/en.json";
import translations_te from "./translations/te.json";
import { Buffer } from "buffer";
import toast from "./components/toast";
import { BrowserRouter } from "react-router-dom";

//theme
import "primeflex/primeflex.css"; // css utility
import "primeicons/primeicons.css";
import "primereact/resources/primereact.css"; // core css
import "primereact/resources/themes/lara-light-indigo/theme.css"; // theme
import "./flags.css";
import "./styles.css";
window.global = window;

const container = document.getElementById("root");
const root =
  window.__reactRoot || (window.__reactRoot = ReactDOM.createRoot(container));

i18next.init({
  interpolation: { escapeValue: false }, // React already does escaping
  lng: "en", // language to use
  resources: {
    en: {
      translations: translations_en, // 'common' is our custom namespace
    },
    ar: {
      translations: translations_ar,
    },
    te: {
      translations: translations_te,
    },
  },
  ns: ["translations"],
  defaultNS: "translations",
});

const NetworkStatusWrapper = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const lastStatus = useRef(navigator.onLine);
  const lastEvent = useRef(null);

  useEffect(() => {
    const isTabVisible = () => document.visibilityState === "visible";

    const showToastIfVisible = (type, message) => {
      if (isTabVisible()) {
        type === "success" ? toast.success(message) : toast.error(message);
      } else {
        lastEvent.current = { type, message };
      }
    };

    const handleOnline = () => {
        if (!lastStatus.current) {
        showToastIfVisible("success", "You are back online");
        lastStatus.current = true;
        setIsOnline(true);
      }
    };

    const handleOffline = () => {
      if (lastStatus.current) {
        showToastIfVisible("error", "Please check your network connection");
        lastStatus.current = false;
        setIsOnline(false);
      }
    };

    const handleVisibilityChange = () => {
      if (isTabVisible() && lastEvent.current) {
        // Show only the last status change
        const { type, message } = lastEvent.current;
        type === "success" ? toast.success(message) : toast.error(message);
        lastEvent.current = null;
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial check on mount
    if (!navigator.onLine && lastStatus.current) {
      showToastIfVisible("error", "Please check your network connection");
      lastStatus.current = false;
      setIsOnline(false);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return children;
};

const App = () => {
  const [client, setClient] = useState(getGraphQLClient());

  if (!window.Buffer) {
    window.Buffer = Buffer;
  }

  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem("token");
      const orgId = localStorage.getItem("orgId");

      if (token && orgId) {
        setTimeout(() => {
          setClient(getGraphQLClient());
        }, 0);
      }
    };

    handleStorageChange(); // run once on mount

    window.addEventListener("localStorageUpdate", handleStorageChange);

    return () => {
      window.removeEventListener("localStorageUpdate", handleStorageChange);
    };
  }, []);

  const entries =
    typeof performance !== "undefined" && performance?.getEntriesByType
      ? performance.getEntriesByType("navigation")
      : [];
  if (entries && entries.length > 0 && entries[0]?.type === "reload") {
    sessionStorage.removeItem("selectedOrg");
  }

  return (
    <ApolloProvider client={client}>
      <Provider store={store}>
        <I18nextProvider i18n={i18next}>
          <PrimeReactProvider>
            <NetworkStatusWrapper>
              <div className="font-sans text-gray-900">
                <BrowserRouter>
                  <AppView />
                </BrowserRouter>
              </div>
            </NetworkStatusWrapper>
          </PrimeReactProvider>
        </I18nextProvider>
      </Provider>
    </ApolloProvider>
  );
};

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
